import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";

// ---------- User Communications Sheet (G5) ----------
export default function UserCommsSheet({ open, onOpenChange, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("teleconsults");

  useEffect(() => {
    if (!open || !user?.id) return;
    setLoading(true);
    setData(null);
    api.get(`/users/${user.id}/communications`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, user]);

  if (!user) return null;

  const teleStatusColor = {
    scheduled: "#3B82F6", in_progress: "#10B981", completed: "#475569",
    cancelled: "#EF4444", no_show: "#F97316", awaiting_participants: "#F59E0B",
  };
  const smsStatusColor = {
    sent: "#10B981", failed: "#EF4444", mocked: "#94A3B8", skipped: "#F59E0B", queued: "#3B82F6",
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[760px] overflow-y-auto p-0 bg-[#FAFBFC]" data-testid="user-comms-sheet">
        <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
          <SheetTitle>Communications — {user.first_name} {user.last_name}</SheetTitle>
          <SheetDescription>
            Teleconsultations · SMS · Notifications sent to {user.email}
            {user.phone ? ` · ${user.phone}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="p-6">
          {loading || !data ? (
            <div className="text-sm text-[#6B7280] p-8 text-center" data-testid="user-comms-loading">Loading communications…</div>
          ) : (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <CommsKPI label="Teleconsults" value={data.counts.teleconsults} testId="kpi-tele" />
                <CommsKPI label="SMS Messages" value={data.counts.sms_messages} testId="kpi-sms" />
                <CommsKPI label="SMS Failed" value={data.counts.sms_failed} accent="#EF4444" testId="kpi-sms-failed" />
                <CommsKPI label="Notifications" value={data.counts.notifications} testId="kpi-notifs" />
              </div>

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                  <TabsTrigger value="teleconsults" data-testid="user-comms-tab-tele">Teleconsults ({data.counts.teleconsults})</TabsTrigger>
                  <TabsTrigger value="sms" data-testid="user-comms-tab-sms">SMS ({data.counts.sms_messages})</TabsTrigger>
                  <TabsTrigger value="notifications" data-testid="user-comms-tab-notif">Notifications ({data.counts.notifications})</TabsTrigger>
                </TabsList>

                <TabsContent value="teleconsults" className="mt-3">
                  {data.teleconsultations.length === 0 ? (
                    <Empty label="No teleconsultations for this user." />
                  ) : (
                    <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="user-comms-tele-list">
                      {data.teleconsultations.map((t) => (
                        <a
                          key={t.id} href={t.action_url}
                          className="block p-3 hover:bg-[#F9FAFB] transition"
                          data-testid={`user-comms-tele-${t.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-[#1F2937]">{t.title}</div>
                            <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider text-white" style={{ background: teleStatusColor[t.status] || "#94A3B8" }}>
                              {(t.status || "").replace(/_/g, " ")}
                            </span>
                          </div>
                          <div className="text-xs text-[#6B7280] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span>Patient: <span className="text-[#1F2937]">{t.patient_name || "—"}</span> {t.patient_mrn && <span className="font-mono text-[#94A3B8]">{t.patient_mrn}</span>}</span>
                            <span>Role: <span className="capitalize">{(t.user_relation || "").replace(/_/g, " ")}</span></span>
                            {t.invite_status && <span>Invite: <span className="capitalize">{t.invite_status}</span></span>}
                            {t.source_module && <span>Source: <span className="uppercase tracking-wider">{t.source_module}</span></span>}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-1">
                            {t.scheduled_at ? `scheduled ${new Date(t.scheduled_at).toLocaleString()}` : (t.created_at ? `created ${new Date(t.created_at).toLocaleString()}` : "")}
                            {t.ended_at && <> · ended {new Date(t.ended_at).toLocaleString()}</>}
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="sms" className="mt-3">
                  {data.sms_messages.length === 0 ? (
                    <Empty label="No SMS messages sent to this user." />
                  ) : (
                    <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="user-comms-sms-list">
                      {data.sms_messages.map((s) => (
                        <div key={s.id} className="p-3" data-testid={`user-comms-sms-${s.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-[#1F2937] text-sm">
                              {(s.event_type || "").replace(/_/g, " ")}
                              <span className="ml-2 text-xs font-normal text-[#94A3B8]">→ {s.recipient || s.recipient_raw || "—"}</span>
                            </div>
                            <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider text-white" style={{ background: smsStatusColor[s.status] || "#94A3B8" }}>
                              {s.status}
                            </span>
                          </div>
                          {s.body && <div className="mt-1 text-xs text-[#475569] whitespace-pre-wrap">{s.body}</div>}
                          {s.error && <div className="mt-1 text-xs text-[#EF4444]">Error: {s.error}</div>}
                          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-1">
                            {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notifications" className="mt-3">
                  {data.notifications.length === 0 ? (
                    <Empty label="No in-app notifications for this user." />
                  ) : (
                    <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="user-comms-notif-list">
                      {data.notifications.map((n) => (
                        <div key={n.id} className="p-3" data-testid={`user-comms-notif-${n.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-[#1F2937] text-sm">{n.title || n.event}</div>
                            {n.severity && <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{n.severity}</span>}
                          </div>
                          {n.body && <div className="mt-1 text-xs text-[#475569]">{n.body}</div>}
                          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-1 flex items-center gap-2">
                            <span>{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</span>
                            {n.is_read ? <span className="text-[#10B981]">read</span> : <span className="text-[#F59E0B]">unread</span>}
                            {n.action_url && <a href={n.action_url} className="text-[#0B7E8C] hover:underline">open ↗</a>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CommsKPI({ label, value, accent = "#0B7E8C", testId }) {
  return (
    <div className="hah-card p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</div>
      <div className="text-2xl font-bold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="hah-card p-8 text-center text-sm text-[#6B7280]">{label}</div>;
}
